import React from 'react'
import { useAppDispatch, useAppSelector } from '@/lib/hooks/use-redux'
import { closeModal, openModal, selectProduct } from '@/lib/redux'

import type { AppDispatch } from '../store'

/**
 * Handles keydown events and dispatches actions based on the key pressed.
 * @param {KeyboardEvent} e - The keyboard event.
 * @param {Function} dispatch - The dispatch function from the Redux store.
 * @param {ModalTypes[]} modals - The array of currently open modals.
 */
const handleKeyDown = (
  e: KeyboardEvent,
  dispatch: AppDispatch,
  modals: ModalTypes[],
) => {
  switch (e.key) {
    case 'k':
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        dispatch(openModal('SEARCH'))
      }
      break
    case 'Escape':
      if (modals.length > 0) {
        const modalToRemove = modals[modals.length - 1]
        switch (modalToRemove) {
          case 'ADD_TO_CART':
            dispatch(closeModal(modalToRemove))
            dispatch(selectProduct(null))
            break
          default:
            dispatch(closeModal(modalToRemove))
        }
      }
      break
    default:
      break
  }
}

/**
 * Custom hook for managing modals.
 * @returns {Object} - An object containing the current modals, a function to close a modal, and a function to open a modal.
 */
export const useModals = () => {
  const dispatch = useAppDispatch()
  const { modals } = useAppSelector((state) => state.modals)

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => handleKeyDown(e, dispatch, modals)
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [dispatch, modals])

  /**
   * Closes a modal and dispatches any necessary actions.
   * @param {ModalTypes} modal - The type of modal to close.
   */
  const handleCloseModal = (modal: ModalTypes, cb?: any) => {
    switch (modal) {
      case 'ADD_TO_CART':
        dispatch(closeModal('ADD_TO_CART'))
        dispatch(selectProduct(null))
        cb?.()
        break
      default:
        dispatch(closeModal(modal))
        cb?.()
        break
    }
  }

  return {
    modals,
    closeModal: handleCloseModal,
    openModal: (modal: ModalTypes, cb?: any) => {
      dispatch(openModal(modal))
      cb?.()
    },
    closeAll: (cb?: any) => {
      for (const modal of modals) {
        handleCloseModal(modal)
      }
      cb?.()
    },
  }
}
